import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AstralTab } from '../../../core/component/astral-components/astral-tabs/astral-tabs.component';
import {
  HogiaIdAssociationProof,
  HogiaIdAssociationState,
} from '../../../core/helper/hogia-id-association-state';
import { IsMobileDevice } from '../../../core/helper/mobile';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { SessionService } from '../../../core/service/session.service';
import { UserAccountService } from '../../service/user-account.service';
import {
  HogiaIdAssociationAuthentication,
  HogiaIdAssociationPreviewResult,
  HogiaIdAssociationResult,
  HogiaIdAssociationService,
} from '../../service/hogia-id-association.service';

type AssociationPhase = 'authenticate' | 'confirm' | 'terminal';
type AuthenticationMethod = 'password' | 'bankid';

@Component({
  selector: 'app-hogia-id-association',
  templateUrl: './hogia-id-association.component.html',
  styleUrls: ['./hogia-id-association.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HogiaIdAssociationComponent implements OnInit, OnDestroy {
  @Input({ required: true }) association!: HogiaIdAssociationState;
  @ViewChild('bankIdContainer') bankIdContainer?: ElementRef<HTMLElement>;

  readonly envName = environment.name;
  readonly authMethods: readonly AstralTab[] = [
    { id: 'password', labelKey: 'LOGIN_FORM.CREDENTIALS' },
    { id: 'bankid', labelKey: 'LOGIN_FORM.BANKID' },
  ];

  readonly authenticationForm: FormGroup;
  authMethod: AuthenticationMethod = 'password';
  phase: AssociationPhase = 'authenticate';
  requestPending = false;
  bankIdRunning = false;
  bankIdAutoStart = false;
  authenticationRejected = false;
  confirmOutcomeUncertain = false;
  isMobile = false;

  private activeRequest?: AbortController;
  private deadlineTimer?: number;
  private activeConfirmationDeadlineEpochMs?: number;
  private destroyed = false;
  private exiting = false;
  private sessionEstablished = false;
  private operation = 0;
  private cancelSent = false;

  constructor(
    formBuilder: FormBuilder,
    controls: FormControlExFactory,
    private readonly associationService: HogiaIdAssociationService,
    private readonly userAccountService: UserAccountService,
    private readonly sessionService: SessionService
  ) {
    this.authenticationForm = formBuilder.group({
      username: controls.createMandatory('LOGIN_FORM.LOGIN_LABEL', ''),
      password: controls.createMandatory('LOGIN_FORM.PASSWORD_LABEL', ''),
    });
  }

  get usernameControl(): FormControl {
    return this.authenticationForm.controls['username'] as FormControl;
  }

  get passwordControl(): FormControl {
    return this.authenticationForm.controls['password'] as FormControl;
  }

  get preview() {
    return this.association.preview;
  }

  get hasAuthenticatedSession(): boolean {
    return this.sessionEstablished && !!this.sessionService.token;
  }

  ngOnInit(): void {
    this.isMobile = IsMobileDevice();
    window.addEventListener('pageshow', this.onPageShow);
    window.addEventListener('focus', this.onForeground);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.ensureAssociationActive();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.removeLifecycleListeners();
    this.beginExitCleanup(!this.sessionEstablished);
  }

  selectAuthMethod(method: string): void {
    if (!this.ensureAssociationActive() || this.requestPending || (method !== 'password' && method !== 'bankid')) {
      return;
    }
    if (method !== this.authMethod) {
      this.stopBankId();
      this.authMethod = method;
      this.authenticationRejected = false;
      this.clearPassword();
    }
  }

  authenticateWithPassword(): void {
    if (!this.ensureAssociationActive() || this.requestPending || this.authenticationForm.invalid) {
      this.authenticationForm.markAllAsTouched();
      return;
    }

    const username = String(this.usernameControl.value ?? '');
    const password = String(this.passwordControl.value ?? '');
    void this.requestPreview({ grant_type: 'client_credentials', username, password });
  }

  startBankId(isAutoStart: boolean): void {
    if (!this.ensureAssociationActive() || this.requestPending || this.bankIdRunning) {
      return;
    }
    this.authenticationRejected = false;
    this.bankIdAutoStart = isAutoStart;
    this.bankIdRunning = true;
  }

  bankIdCompleted(orderReference: string): void {
    this.stopBankId();
    if (!this.ensureAssociationActive() || !orderReference || this.requestPending) {
      return;
    }
    void this.requestPreview({ grant_type: 'bankid_credentials', order_reference: orderReference });
  }

  async confirm(): Promise<void> {
    if (this.requestPending || !this.ensureAssociationActive()) {
      return;
    }

    const nowEpochMs = Date.now();
    const remainingLifetimeMs = this.association.remainingLifetimeMs(nowEpochMs);
    const accessToken = this.sessionService.token;
    const confirmationProof = this.association.takeConfirmationProof(nowEpochMs);
    if (!accessToken || !confirmationProof || remainingLifetimeMs <= 0) {
      if (!accessToken) {
        this.sessionEstablished = false;
      }
      this.finishWithFailure(false);
      return;
    }

    this.activeConfirmationDeadlineEpochMs = nowEpochMs + remainingLifetimeMs;
    const request = this.beginRequest();
    let result: HogiaIdAssociationResult<void>;
    try {
      result = await this.associationService.confirm(
        confirmationProof,
        accessToken,
        request.signal
      );
    } catch {
      result = { ok: false };
    }
    if (!this.completeRequest(request.operation)) {
      return;
    }

    this.activeConfirmationDeadlineEpochMs = undefined;
    if (result.ok) {
      this.clearDeadlineTimer();
      this.exiting = true;
      try {
        this.replaceLocation('/');
        return;
      } catch {
        this.exiting = false;
        // Use the same uncertain terminal state as a lost Confirm response.
      }
    }

    this.finishWithFailure(true);
  }

  async cancel(): Promise<void> {
    await this.exitAssociation(false);
  }

  async logout(): Promise<void> {
    await this.exitAssociation(true);
  }

  continueToDh(): void {
    if (this.destroyed || this.exiting) {
      return;
    }

    this.exiting = true;
    this.beginExitCleanup(!this.sessionEstablished);
    this.navigateAfterCleanup('/');
  }

  /** Shared boundary for current and future user actions that leave association. */
  leaveAssociation(): void {
    void this.exitAssociation(false);
  }

  async onRequestFullscreen(): Promise<void> {
    const element = this.bankIdContainer?.nativeElement;
    if (!element) {
      return;
    }
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await element.requestFullscreen();
      }
    } catch {
      // Fullscreen is optional; keep the BankID ceremony usable in its inline presentation.
    }
  }

  private async requestPreview(authentication: HogiaIdAssociationAuthentication): Promise<void> {
    const proof = this.association.proofForPreview();
    if (!proof || this.requestPending) {
      this.finishWithFailure(false);
      return;
    }

    this.authenticationRejected = false;
    // Authentication owns the request value now; do not retain a second password copy in the form.
    this.clearPassword();
    const request = this.beginRequest();
    let result: HogiaIdAssociationResult<HogiaIdAssociationPreviewResult>;
    try {
      result = await this.associationService.preview(proof, authentication, request.signal);
    } catch {
      result = { ok: false };
    }
    if (this.destroyed || request.operation !== this.operation) {
      return;
    }

    if (result.ok === false && result.status === 401) {
      this.completeRequest(request.operation);
      this.association.clearPreview();
      this.authenticationRejected = true;
      this.stopBankId();
      return;
    }

    if (result.ok) {
      let accountInitialized = false;
      try {
        await firstValueFrom(
          this.userAccountService.establishSession(
            result.value.session.accessToken,
            result.value.session.expiresInSeconds,
            false
          )
        );
        accountInitialized = true;
      } catch {
        accountInitialized = false;
      }

      if (!this.completeRequest(request.operation)) {
        this.rollbackLateAssociationSession(result.value.session.accessToken);
        return;
      }

      if (accountInitialized) {
        this.sessionEstablished = true;
        if (this.association.setPreview(result.value.preview)) {
          this.phase = 'confirm';
          this.ensureAssociationActive();
          return;
        }

        await this.exitAssociation(false);
        return;
      }
    }

    this.completeRequest(request.operation);
    this.finishWithFailure(false);
  }

  private beginRequest(): { operation: number; signal: AbortSignal } {
    this.activeRequest?.abort();
    const request = new AbortController();
    this.activeRequest = request;
    this.requestPending = true;
    return { operation: ++this.operation, signal: request.signal };
  }

  private completeRequest(operation: number): boolean {
    if (this.destroyed || operation !== this.operation) {
      return false;
    }
    this.activeRequest = undefined;
    this.requestPending = false;
    return true;
  }

  private ensureAssociationActive(): boolean {
    if (this.destroyed || this.exiting || this.phase === 'terminal') {
      return false;
    }

    const remainingLifetimeMs = this.activeConfirmationDeadlineEpochMs === undefined
      ? this.association.remainingLifetimeMs()
      : this.activeConfirmationDeadlineEpochMs - Date.now();
    if (remainingLifetimeMs <= 0) {
      if (this.activeConfirmationDeadlineEpochMs !== undefined) {
        this.finishWithFailure(true);
      } else {
        void this.exitAssociation(false);
      }
      return false;
    }

    this.armDeadlineTimer(remainingLifetimeMs);
    return true;
  }

  private armDeadlineTimer(remainingLifetimeMs: number): void {
    this.clearDeadlineTimer();
    this.deadlineTimer = window.setTimeout(this.onDeadline, remainingLifetimeMs);
  }

  private clearDeadlineTimer(): void {
    if (this.deadlineTimer !== undefined) {
      window.clearTimeout(this.deadlineTimer);
      this.deadlineTimer = undefined;
    }
  }

  private beginExitCleanup(clearSession: boolean): HogiaIdAssociationProof | undefined {
    this.activeRequest?.abort();
    this.activeRequest = undefined;
    this.operation += 1;
    this.requestPending = false;
    this.stopBankId();
    this.activeConfirmationDeadlineEpochMs = undefined;
    this.clearDeadlineTimer();
    this.clearPassword();
    const proof = this.association?.takeCancellationProof();
    if (clearSession) {
      this.userAccountService.clearAuthenticatedSession();
      this.sessionEstablished = false;
    }
    return proof;
  }

  private finishWithFailure(confirmOutcomeUncertain: boolean): void {
    this.beginExitCleanup(!this.sessionEstablished);
    this.confirmOutcomeUncertain = confirmOutcomeUncertain;
    this.phase = 'terminal';
  }

  private rollbackLateAssociationSession(accessToken: string): void {
    // Do not let this abandoned flow clear a newer, unrelated login.
    if (this.sessionService.token === accessToken) {
      this.userAccountService.clearAuthenticatedSession();
    }
  }

  private navigateAfterCleanup(url: string): void {
    try {
      this.replaceLocation(url);
    } catch {
      this.exiting = false;
      this.finishWithFailure(false);
    }
  }

  private replaceLocation(url: string): void {
    window.location.replace(url);
  }

  private clearPassword(): void {
    this.passwordControl.setValue('');
    this.passwordControl.markAsPristine();
    this.passwordControl.markAsUntouched();
  }

  private stopBankId(): void {
    this.bankIdRunning = false;
  }

  private removeLifecycleListeners(): void {
    window.removeEventListener('pageshow', this.onPageShow);
    window.removeEventListener('focus', this.onForeground);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  private readonly onDeadline = (): void => {
    this.deadlineTimer = undefined;
    this.ensureAssociationActive();
  };

  private readonly onForeground = (): void => {
    this.ensureAssociationActive();
  };

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.ensureAssociationActive();
    }
  };

  private readonly onPageShow = (event: PageTransitionEvent): void => {
    if (event.persisted) {
      this.leaveAssociation();
      return;
    }

    this.ensureAssociationActive();
  };

  private async exitAssociation(logout: boolean): Promise<void> {
    if (this.destroyed || this.exiting) {
      return;
    }

    this.exiting = true;
    const proof = this.beginExitCleanup(logout || !this.sessionEstablished);

    if (proof && !this.cancelSent) {
      this.cancelSent = true;
      const request = this.beginRequest();
      try {
        await this.associationService.cancel(proof, request.signal);
      } catch {
        // Cancellation is best effort; local proof cleanup already completed synchronously.
      }
      if (!this.completeRequest(request.operation)) {
        return;
      }
    }

    this.navigateAfterCleanup('/');
  }
}
